/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/camelcase */
import React from 'react';
import { Box, Checkbox, Grid, makeStyles, Typography } from '@material-ui/core';
import ApplicationState from 'store/interfaces';
import { connect } from 'react-redux';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {
  BulkClientsFields,
  EnvironmentFields,
  ListBulkClientsProps,
} from 'modules/environments/interfaces';
import i18n from 'i18n';
import Button from 'components/Button';
import { modalClose, modalOpen } from 'modules/modal/actions';
import clsx from 'clsx';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import {
  editBulkClientsRequest,
  setSelectedEnvironment,
  getBulkClientsRequest,
} from 'modules/environments/actions';
import { ModalFields } from 'modules/modal/interfaces';
import { Form, Formik } from 'formik';
import TitleDivider from 'components/TitleDivider';
import DynamicTextField from 'components/DynamicTextField';
import palette from 'theme/palette';
import { useTableFilters } from 'hooks/useTableFilters';
import { SearchBar } from 'components/SearchBar';
import VerificationCode from 'components/VerificationCode';
import styles, { overrideModal } from './styles';
import EnvironmentList from './environmentList';
import screenFields from '../screenFields';
import schema from '../schema';
import { initialQuery } from '..';

const useStyles = makeStyles(styles);

export interface EnvironmentFieldsProps {
  environment_name?: string;
  environment_id?: string;
}

export interface EnvironmentEditProps {
  bulkClients: ListBulkClientsProps;
  isLoadingBulkClients: boolean;
  environment?: EnvironmentFields | null;
  selectedEnvironment: EnvironmentFields | null;
  verificationCodeRef: React.MutableRefObject<string[]>;
  setVerificationCode: React.Dispatch<React.SetStateAction<string[]>>;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
  setSelectedEnvironment: (prop: EnvironmentFields | null) => void;
  editBulkClientsRequest: (props) => void;
  getBulkClientsRequest: (props) => void;
}

const EnvironmentEdit = ({
  environment,
  bulkClients,
  selectedEnvironment,
  isLoadingBulkClients,
  verificationCodeRef,
  setVerificationCode,
  modalOpen,
  modalClose,
  editBulkClientsRequest,
  setSelectedEnvironment,
  getBulkClientsRequest,
}: EnvironmentEditProps) => {
  interface ClientInfo {
    client_uuid: string;
    name: string;
  }

  const headers = [
    {
      key: 'nome',
      i18n: 'CLIENT_NAME_KEY',
      order: true,
    },
  ];

  const {
    query,
    searchValue,
    itemOrder,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters(initialQuery);

  const classes = useStyles();
  const [openClientSelector, setOpenClientSelector] = React.useState(false);
  const [openEnvironmentSelector, setOpenEnvironmentSelector] = React.useState(
    true
  );
  const [selectedAll, setSelectedAll] = React.useState(false);
  const [checks, setChecks] = React.useState<ClientInfo[]>([]);
  const [
    environmentFields,
    setEnvironmentFields,
  ] = React.useState<EnvironmentFieldsProps | null>({
    environment_name: environment?.name,
    environment_id: environment?.environment_uuid,
  });

  const clientUuids: string[] = checks.map(client => client?.client_uuid);
  const handleConfirm = (values: any) => {
    const verificationCodeToSend = verificationCodeRef.current;
    editBulkClientsRequest({
      id: environment?.environment_uuid,
      dataValues: {
        operator_data: {
          action_reason: values?.action_reason,
          restricted_action_code: verificationCodeToSend.join(''),
        },
        clients_data: [
          {
            cli_uuids: clientUuids,
            environment_uuid: selectedEnvironment?.environment_uuid,
          },
        ],
      },
    });
    modalClose();
  };

  const handleSubmit = (values: any) => {
    setVerificationCode(Array(6).fill(''));
    editBulkClientsRequest({
      id: environment?.environment_uuid,
      dataValues: {
        operator_data: {
          action_reason: values?.action_reason,
        },
        clients_data: [
          {
            cli_uuids: clientUuids,
            environment_uuid: selectedEnvironment?.environment_uuid,
          },
        ],
      },
    });
    modalOpen({
      title: i18n.t('VERIFICATION_CODE_KEY'),
      content: (
        <VerificationCode
          length={6}
          setVerificationCode={setVerificationCode}
          onRetry={() => {
            editBulkClientsRequest({
              id: environment?.environment_uuid,
              dataValues: {
                operator_data: {
                  action_reason: values?.action_reason,
                },
                clients_data: [
                  {
                    cli_uuids: clientUuids,
                    environment_uuid: selectedEnvironment?.environment_uuid,
                  },
                ],
              },
            });
          }}
          onConfirm={() => {
            handleConfirm(values);
          }}
        />
      ),
      fullWidth: true,
    });
  };

  const getBulkClients = () => {
    getBulkClientsRequest({
      id: environment?.environment_uuid,
      query: { ...query },
    });
  };

  React.useEffect(() => {
    getBulkClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getBulkClientsRequest, query]);

  const handleAllCheck = () => {
    if (!selectedAll) {
      const clientsInfo: ClientInfo[] = bulkClients.results.map(item => ({
        client_uuid: item?.client_uuid || '',
        name: item?.name || '',
      }));
      setChecks(clientsInfo);
    } else {
      setChecks([]);
    }
    setSelectedAll(!selectedAll);
  };

  const handleCheck = (uuid: string, name: string) => {
    const findCheckId = checks.find(item => item?.client_uuid === uuid);
    if (!findCheckId) {
      setChecks([...checks, { client_uuid: uuid, name }]);
    } else {
      const filteredChecks = checks.filter(item => item?.client_uuid !== uuid);
      setChecks(filteredChecks);
    }
  };

  React.useEffect(() => {
    setSelectedEnvironment(null);
  }, []);

  const handleOpenMigrateConfirmation = () => {
    modalOpen({
      title: i18n.t('BULK_CLIENTS.CONFIRM_MIGRATION_BETWEEN_ENVIRONMENTS_KEY'),
      fullWidth: true,
      maxWidth: 'md',
      overrideClasses: overrideModal,
      content: (
        <>
          <Grid container justify="center">
            <Grid
              item
              style={{
                textAlign: 'center',
                display: 'block',
                margin: '0px -35px',
              }}
            >
              <CheckCircleIcon
                style={{
                  color: palette.green,
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  position: 'relative',
                  transition: 'all 0.7s ease-in-out',
                }}
              />
              <Typography style={{ fontSize: 11 }}>
                {i18n.t('BULK_CLIENTS.CURRENT_ENVIRONMENT_KEY')}
              </Typography>
            </Grid>
            <Typography
              style={{
                color: palette.green,
                alignItems: 'flex-start',
                display: 'flex',
                marginBottom: '20px',
                transition: 'all 0.5s ease-in-out',
              }}
            >
              {'━━━━━━━━━━━━'}
            </Typography>
            <Grid
              item
              style={{
                textAlign: 'center',
                display: 'block',
                margin: '0px -35px',
              }}
            >
              <CheckCircleIcon
                style={{
                  color: palette.green,
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  position: 'relative',
                  transition: 'all 0.7s ease-in-out',
                }}
              />
              <Typography style={{ fontSize: 11 }}>
                {i18n.t('BULK_CLIENTS.DESTINATION_ENVIRONMENT_KEY')}
              </Typography>
            </Grid>
            <Typography
              style={{
                color: palette.green,
                alignItems: 'flex-start',
                display: 'flex',
                marginBottom: '20px',
                transition: 'all 0.5s ease-in-out',
              }}
            >
              {'━━━━━━━━━━━━'}
            </Typography>
            <Grid
              item
              style={{
                textAlign: 'center',
                display: 'block',
                margin: '0px -35px',
              }}
            >
              <CheckCircleIcon
                style={{
                  color: palette.green,
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  position: 'relative',
                  transition: 'all 0.7s ease-in-out',
                }}
              />
              <Typography style={{ fontSize: 11 }}>
                {i18n.t('BULK_CLIENTS.CLIENTS_TO_MIGRATE_KEY')}
              </Typography>
            </Grid>
            <Typography
              style={{
                color: palette.green,
                alignItems: 'flex-start',
                display: 'flex',
                marginBottom: '20px',
                transition: 'all 0.5s ease-in-out',
              }}
            >
              {'━━━━━━━━━━━━'}
            </Typography>
            <Grid item>
              <CheckCircleIcon
                style={{
                  color: palette.green,
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  position: 'relative',
                  transition: 'all 0.7s ease-in-out',
                }}
              />
              <Typography style={{ marginLeft: '-20px', fontSize: 11 }}>
                {i18n.t('BULK_CLIENTS.CONFIRMATION_KEY')}
              </Typography>
            </Grid>
          </Grid>
          <Grid container justify="center">
            <Typography style={{ fontSize: 12, margin: 12 }} component="div">
              {i18n.t(
                'BULK_CLIENTS.CONFIRM_CLIENTS_MIGRATION_TO_ENVIRONMENT_KEY'
              )}
              <Box fontWeight="bold" display="inline">
                {selectedEnvironment?.name}
              </Box>
              {'?'}
            </Typography>
          </Grid>
          <TableItems
            header={
              <>
                <TableCell className={classes.tableRow}>
                  {i18n.t('CLIENT_NAME_KEY')}
                </TableCell>
              </>
            }
            renderItems={(item: BulkClientsFields, index) => (
              <TableRow key={index}>
                <TableCell>{item?.name}</TableCell>
              </TableRow>
            )}
            isLoading={isLoadingBulkClients}
            data={{
              count: 0,
              next: '',
              previous: '',
              results: checks,
            }}
            onChangeLimit={handleLimitChange}
            onChangePage={handlePageChange}
            notFoundText={i18n.t('NO_CLIENT_KEY')}
            notFooter
            smallLoading={true}
          />
          <TitleDivider
            overrideClasses={{
              title: { margin: '12px 4px 8px 4px', fontSize: 12 },
            }}
          >
            {i18n.t('MIGRATION_TO_CONFIRM_KEY')}
          </TitleDivider>
          <Formik
            initialValues={{ action_reason: '' }}
            enableReinitialize
            validationSchema={schema}
            validateOnChange
            validateOnBlur
            onSubmit={handleSubmit}
          >
            {({ setFieldValue, errors, values, submitForm }) => {
              return (
                <React.Fragment>
                  <Form>
                    <Grid container>
                      <Grid container spacing={2} className={classes.fields}>
                        <DynamicTextField
                          {...screenFields[0]}
                          setFieldValue={setFieldValue}
                          value={values?.action_reason}
                          errors={errors}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      justify="flex-end"
                      style={{ marginTop: 12 }}
                    >
                      <Button
                        submitButton
                        id="confirm-button"
                        onClick={submitForm}
                        isLoading={isLoadingBulkClients}
                      >
                        {i18n.t('CONTINUE_KEY')}
                      </Button>
                      <Button
                        id="cancel-button"
                        variant="outlined"
                        onClick={modalClose}
                      >
                        {i18n.t('CANCEL_KEY')}
                      </Button>
                    </Grid>
                  </Form>
                </React.Fragment>
              );
            }}
          </Formik>
        </>
      ),
    });
  };

  return (
    <Grid container spacing={2} className={classes.container}>
      <Grid container justify="center">
        <Grid item className={classes.stepsGrid}>
          <CheckCircleIcon className={classes.stepIconComplete} />
          <Typography className={classes.stepLabel}>
            {i18n.t('BULK_CLIENTS.CURRENT_ENVIRONMENT_KEY')}
          </Typography>
        </Grid>
        <Typography className={classes.progressLineComplete}>
          {'━━━━━━━━━━━━'}
        </Typography>

        {selectedEnvironment ? (
          <Grid item className={classes.stepsGrid}>
            <CheckCircleIcon className={classes.stepIconComplete} />
            <Typography className={classes.stepLabel}>
              {i18n.t('BULK_CLIENTS.DESTINATION_ENVIRONMENT_KEY')}
            </Typography>
          </Grid>
        ) : (
          <Grid item className={classes.stepsGrid}>
            <RadioButtonUncheckedIcon className={classes.stepIcon} />
            <Typography className={classes.stepLabel}>
              {i18n.t('BULK_CLIENTS.DESTINATION_ENVIRONMENT_KEY')}
            </Typography>
          </Grid>
        )}
        <Typography
          className={clsx({
            [classes.progressLineComplete]: !!selectedEnvironment,
            [classes.progressLine]: !selectedEnvironment,
          })}
        >
          {'━━━━━━━━━━━━'}
        </Typography>
        {checks.length > 0 ? (
          <Grid item className={classes.stepsGrid}>
            <CheckCircleIcon className={classes.stepIconComplete} />
            <Typography className={classes.stepLabel}>
              {i18n.t('BULK_CLIENTS.CLIENTS_TO_MIGRATE_KEY')}
            </Typography>
          </Grid>
        ) : (
          <Grid item className={classes.stepsGrid}>
            <RadioButtonUncheckedIcon className={classes.stepIcon} />
            <Typography className={classes.stepLabel}>
              {i18n.t('BULK_CLIENTS.CLIENTS_TO_MIGRATE_KEY')}
            </Typography>
          </Grid>
        )}
        <Typography
          className={clsx({
            [classes.progressLineComplete]: checks.length > 0,
            [classes.progressLine]: checks.length === 0,
          })}
        >
          {'━━━━━━━━━━━━'}
        </Typography>
        <Grid item>
          <RadioButtonUncheckedIcon className={classes.stepIcon} />
          <Typography className={classes.stepLabelConfirm}>
            {i18n.t('BULK_CLIENTS.CONFIRMATION_KEY')}
          </Typography>
        </Grid>
      </Grid>
      <Grid container className={classes.titleGrid} justify="space-between">
        <Grid item className={classes.optionsCard}>
          <Typography className={classes.title} component="div">
            {`${i18n.t('BULK_CLIENTS.CURRENT_ENVIRONMENT_KEY')}: `}
            <Box fontWeight="bold" display="inline">
              {environment?.name}
            </Box>
          </Typography>
        </Grid>
        <Grid item>
          <CheckRoundedIcon className={classes.successIcon} />
        </Grid>
      </Grid>
      <Grid container className={classes.titleGrid} justify="space-between">
        <Grid item className={classes.optionsCard}>
          <Typography className={classes.title} component="div">
            {`${i18n.t('BULK_CLIENTS.DESTINATION_ENVIRONMENT_KEY')}: `}
          </Typography>
          <Grid item>
            <Button
              id={'btn-select-environment'}
              onClick={() => {
                setOpenEnvironmentSelector(true);
                setOpenClientSelector(false);
              }}
              className={clsx(classes.autocompleteCompany, {
                [classes.errorSamePartnerButton]:
                  selectedEnvironment?.environment_uuid ===
                  environment?.environment_uuid,
              })}
              endIcon={<ExpandMoreIcon className={classes.expandIcon} />}
            >
              {selectedEnvironment ? (
                <Typography className={classes.bold}>
                  {selectedEnvironment?.name}
                </Typography>
              ) : (
                <Typography className={classes.titleBold}>
                  {i18n.t('BULK_CLIENTS.CHOOSE_ENVIRONMENT_KEY')}
                </Typography>
              )}
            </Button>
          </Grid>
        </Grid>
        {selectedEnvironment && (
          <Grid item>
            <CheckRoundedIcon className={classes.successIcon} />
          </Grid>
        )}

        {openEnvironmentSelector && (
          <Grid container>
            <EnvironmentList
              companyFields={environmentFields}
              setCompanyFields={setEnvironmentFields}
              setOpenEnvironmentSelector={setOpenEnvironmentSelector}
              setOpenClientSelector={setOpenClientSelector}
            />
          </Grid>
        )}
      </Grid>
      <Grid container className={classes.titleGrid} justify="space-between">
        <Grid item className={classes.optionsCard}>
          <Typography className={classes.title}>
            {i18n.t('BULK_CLIENTS.SELECT_CLIENTS_TO_MIGRATE_KEY')}
          </Typography>
          <Grid item>
            <Button
              id={'btn-select-clients'}
              onClick={() => {
                setOpenEnvironmentSelector(false);
                setOpenClientSelector(true);
              }}
              className={classes.selectClientButton}
              disabled={!selectedEnvironment}
            >
              <ExpandMoreIcon />
            </Button>
          </Grid>
        </Grid>
        {checks.length > 0 && (
          <Grid item>
            <CheckRoundedIcon className={classes.successIcon} />
          </Grid>
        )}

        {openClientSelector && (
          <Grid container>
            <>
              <SearchBar
                id="search-client"
                isLoading={isLoadingBulkClients}
                searchValue={searchValue}
                handleSearch={handleSearch}
                setSearchValue={setSearchValue}
              />
              <TableItems
                header={
                  <>
                    <TableCell className={classes.checkboxCell}>
                      <Checkbox
                        onChange={handleAllCheck}
                        id="select-all-clients-environment"
                      />
                    </TableCell>
                    {headers.map(item => (
                      <TableCell
                        key={item?.key}
                        order={item?.order}
                        handleOrder={() =>
                          item?.order ? handleOrder(item?.key) : null
                        }
                        markOderItem={itemOrder === item?.key}
                      >
                        {i18n.t(item?.i18n)}
                      </TableCell>
                    ))}
                  </>
                }
                renderItems={(item: BulkClientsFields, index) => (
                  <TableRow key={index}>
                    <TableCell className={classes.checkboxCell}>
                      <Checkbox
                        onChange={() =>
                          handleCheck(item?.client_uuid || '', item?.name || '')
                        }
                        checked={
                          !!checks.find(
                            check => check?.client_uuid === item?.client_uuid
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>{item?.name}</TableCell>
                  </TableRow>
                )}
                isLoading={isLoadingBulkClients}
                data={bulkClients}
                overrideRowsCount={10}
                onChangeLimit={handleLimitChange}
                onChangePage={handlePageChange}
                notFoundText={i18n.t('NO_CLIENT_KEY')}
                smallLoading={true}
              />
            </>
          </Grid>
        )}
      </Grid>
      <Grid container justify="flex-end" className={classes.buttons}>
        <Button
          id="migrate-button"
          submitButton
          onClick={() => {
            handleOpenMigrateConfirmation();
          }}
          disabled={!selectedEnvironment || checks.length === 0}
        >
          {i18n.t('NEXT_KEY')}
        </Button>
        <Button id="cancel-button" variant="outlined" onClick={modalClose}>
          {i18n.t('CLOSE_KEY')}
        </Button>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { data },
  environments: { bulkClients, selectedEnvironment, isLoadingBulkClients },
}: ApplicationState) => ({
  role: data?.role,
  bulkClients,
  selectedEnvironment,
  isLoadingBulkClients,
});

export default connect(mapStateToProps, {
  modalOpen,
  modalClose,
  setSelectedEnvironment,
  editBulkClientsRequest,
  getBulkClientsRequest,
})(EnvironmentEdit);
